import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ComplianceChecklist = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedItems, setExpandedItems] = useState(new Set());

  const complianceItems = [
    {
      id: 1,
      category: 'EUA',
      title: 'EUA Registry Account Verification',
      description: 'Verify all EUA registry accounts are properly maintained and updated',
      status: 'completed',
      priority: 'high',
      dueDate: '2025-09-25',
      completedDate: '2025-09-20',
      assignee: 'Sarah Chen',
      requirements: [
        'Account holder information verification',
        'Authorized representative updates',
        'Contact information validation'
      ]
    },
    {
      id: 2,
      category: 'EUA',
      title: 'MiFID II Transaction Reporting',
      description: 'Submit transaction reports for EUA trades to relevant authorities',
      status: 'pending',
      priority: 'critical',
      dueDate: '2025-09-22',
      assignee: 'Michael Rodriguez',
      requirements: [
        'Trade confirmation documentation',
        'Counterparty identification',
        'Transaction timing records'
      ]
    },
    {
      id: 3,
      category: 'Oil',
      title: 'CFTC Position Reporting',
      description: 'File large trader position reports for oil futures contracts',
      status: 'in-progress',
      priority: 'high',
      dueDate: '2025-09-23',
      assignee: 'David Kim',
      progress: 75,
      requirements: [
        'Position size calculations',
        'Contract month breakdown',
        'Beneficial ownership disclosure'
      ]
    },
    {
      id: 4,
      category: 'Oil',
      title: 'Dodd-Frank Swap Reporting',
      description: 'Report oil derivative transactions under Dodd-Frank regulations',
      status: 'overdue',
      priority: 'critical',
      dueDate: '2025-09-19',
      assignee: 'Lisa Wang',
      requirements: [
        'Swap data repository submission',
        'Real-time reporting compliance',
        'End-of-day reconciliation'
      ]
    },
    {
      id: 5,
      category: 'General',
      title: 'Risk Management Documentation',
      description: 'Update risk management policies and procedures documentation',
      status: 'completed',
      priority: 'medium',
      dueDate: '2025-09-18',
      completedDate: '2025-09-17',
      assignee: 'James Thompson',
      requirements: [
        'Policy review and updates',
        'Procedure documentation',
        'Approval workflow completion'
      ]
    },
    {
      id: 6,
      category: 'General',
      title: 'AML/KYC Customer Review',
      description: 'Conduct periodic review of customer due diligence records',
      status: 'pending',
      priority: 'medium',
      dueDate: '2025-09-30',
      assignee: 'Emma Davis',
      requirements: [
        'Customer information verification',
        'Risk assessment updates',
        'Documentation completeness check'
      ]
    }
  ];

  const categories = [
    { value: 'all', label: 'All Categories', count: complianceItems?.length },
    { value: 'EUA', label: 'EUA Trading', count: complianceItems?.filter(item => item?.category === 'EUA')?.length },
    { value: 'Oil', label: 'Oil Futures', count: complianceItems?.filter(item => item?.category === 'Oil')?.length },
    { value: 'General', label: 'General Compliance', count: complianceItems?.filter(item => item?.category === 'General')?.length }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-success bg-success/10 border-success/20';
      case 'in-progress': return 'text-warning bg-warning/10 border-warning/20';
      case 'pending': return 'text-accent bg-accent/10 border-accent/20';
      case 'overdue': return 'text-error bg-error/10 border-error/20';
      default: return 'text-muted-foreground bg-muted border-border';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'text-error';
      case 'high': return 'text-warning';
      case 'medium': return 'text-accent';
      case 'low': return 'text-muted-foreground';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return 'CheckCircle';
      case 'in-progress': return 'Clock';
      case 'pending': return 'AlertCircle';
      case 'overdue': return 'XCircle';
      default: return 'Circle';
    }
  };

  const toggleExpanded = (itemId) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded?.has(itemId)) {
      newExpanded?.delete(itemId);
    } else {
      newExpanded?.add(itemId);
    }
    setExpandedItems(newExpanded);
  };

  const filteredItems = selectedCategory === 'all' 
    ? complianceItems 
    : complianceItems?.filter(item => item?.category === selectedCategory);

  const getDaysUntilDue = (dueDate) => {
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-subtle">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Shield" size={18} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Compliance Checklist</h2>
              <p className="text-sm text-muted-foreground">Track regulatory requirements and deadlines</p>
            </div>
          </div>
          <Button variant="outline" iconName="Download" iconPosition="left" size="sm">
            Export Report
          </Button>
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap gap-2">
          {categories?.map(category => (
            <button
              key={category?.value}
              onClick={() => setSelectedCategory(category?.value)}
              className={`
                px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 flex items-center gap-2
                ${selectedCategory === category?.value
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }
              `}
            >
              <span>{category?.label}</span>
              <span className={`
                px-1.5 py-0.5 rounded text-xs font-medium
                ${selectedCategory === category?.value
                  ? 'bg-primary-foreground/20 text-primary-foreground'
                  : 'bg-background text-foreground'
                }
              `}>
                {category?.count}
              </span>
            </button>
          ))}
        </div>
      </div>
      <div className="p-6">
        <div className="space-y-4">
          {filteredItems?.map(item => {
            const daysUntilDue = getDaysUntilDue(item?.dueDate);
            const isExpanded = expandedItems?.has(item?.id);

            return (
              <div key={item?.id} className="border border-border rounded-lg p-4 hover:shadow-subtle transition-shadow duration-200">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <Icon 
                      name={getStatusIcon(item?.status)} 
                      size={20} 
                      className={getStatusColor(item?.status)?.split(' ')?.[0]} 
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-medium text-foreground">{item?.title}</h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(item?.status)}`}>
                          {item?.status?.replace('-', ' ')}
                        </span>
                        <span className={`text-xs font-medium ${getPriorityColor(item?.priority)}`}>
                          {item?.priority?.toUpperCase()}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">{item?.description}</p>
                      
                      <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Icon name="Calendar" size={14} />
                          <span>Due: {new Date(item.dueDate)?.toLocaleDateString()}</span>
                          {daysUntilDue < 0 && (
                            <span className="text-error font-medium">({Math.abs(daysUntilDue)} days overdue)</span>
                          )}
                          {daysUntilDue >= 0 && daysUntilDue <= 3 && (
                            <span className="text-warning font-medium">({daysUntilDue} days left)</span>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <Icon name="User" size={14} />
                          <span>{item?.assignee}</span>
                        </div>
                        {item?.completedDate && (
                          <div className="flex items-center gap-1">
                            <Icon name="Check" size={14} />
                            <span>Completed: {new Date(item.completedDate)?.toLocaleDateString()}</span>
                          </div>
                        )}
                      </div>

                      {item?.status === 'in-progress' && item?.progress && (
                        <div className="mt-3">
                          <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                            <span>Progress</span>
                            <span>{item?.progress}%</span>
                          </div>
                          <div className="w-full bg-muted rounded-full h-2">
                            <div 
                              className="bg-accent rounded-full h-2 transition-all duration-300"
                              style={{ width: `${item?.progress}%` }}
                            />
                          </div>
                        </div>
                      )}

                      {isExpanded && (
                        <div className="mt-4 pt-4 border-t border-border">
                          <h4 className="text-sm font-medium text-foreground mb-2">Requirements:</h4>
                          <ul className="space-y-1">
                            {item?.requirements?.map((req, index) => (
                              <li key={index} className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Icon name="ChevronRight" size={14} />
                                <span>{req}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 ml-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleExpanded(item?.id)}
                      iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
                    />
                    <Button variant="ghost" size="sm" iconName="MoreHorizontal" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {filteredItems?.length === 0 && (
          <div className="text-center py-12">
            <Icon name="Shield" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No compliance items found</h3>
            <p className="text-muted-foreground">No items match the selected category filter.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComplianceChecklist;